import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';

import { getLastRefresh, getPollingInterval } from '../../../bundles/application';
import { requestLimitedUsers } from '../../../bundles/ad';
import { getIsAdmin, getIsTenantOrSuperAdmin } from '../../../bundles/auth';
import {
  getUserEventTypes,
  requestUserEventTypes,
  requestSeriesEvents,
} from '../../../bundles/events';
import { getStaticComponents, setImportErrors, getImportErrors } from '../../../bundles/components';
import { requestTags } from '../../../bundles/tags';
import { useClientSize } from '../../../utils';
import {
  SEVERITIES,
  DTMP_SEVERITIES,
  EVENT_TYPE_USER,
  EVENT_TYPE_DTMP,
  EVENT_TYPE_MACH,
} from '../../../constants';
import { useEventModal } from '../../EventModal';
import EventBrush from './EventBrush';
import EventLegends from './EventLegends';
import {
  EventList,
  OPTIONAL_MACH_EVENT_COLUMNS,
  OPTIONAL_USER_EVENT_COLUMNS,
  OPTIONAL_DTMP_EVENT_COLUMNS,
} from './EventList';
import EventTimelineHeader from './EventTimelineHeader';
import IotTimeline from './IotTimeline';
import UserTimeline from './UserTimeline';
import LoadingLayer from '../../LoadingLayer';
import config from '../../../config';
import {
  EventTimelineProvider,
  useDateDomainSource,
  useUserEventTypes,
} from './EventTimelineState';
import { parseMaintenanceExcel } from '../../../externalUtils';
import ConfirmationDialog from '../../ConfirmationDialog';
import ImportMaintenancePlanModal from './ImportMaintenancePlanModal/ImportMaintenancePlanModal';
import {
  clampLimitedListEventCount,
  getDefaultEventType,
  includesEventType,
  normalizeEventTypeSelection,
} from './eventTypeSelection';

export const SPLIT_VIEW = 'split';
export const LIST_VIEW = 'list view';
export const LIMITED_LIST_VIEW = 'limited list view';
export const TIMELINE_VIEW = 'timeline';

// Matches $panel-scroll-body-height in VirtualizedTable.scss
const NON_FULLSCREEN_LIST_HEIGHT = 600;

export const panelViews = [
  {
    type: TIMELINE_VIEW,
    title: 'Timeline view',
    icon: 'he-bubble-chart',
    tooltip: 'Show timeline view',
  },
  {
    type: SPLIT_VIEW,
    title: 'Split view',
    icon: 'he-group',
    tooltip: 'Show split view',
  },
  {
    type: LIST_VIEW,
    title: 'List view',
    icon: 'he-list',
    tooltip: 'Show list view',
  },
  {
    type: LIMITED_LIST_VIEW,
    title: 'Limited List View',
    icon: 'he-list',
    tooltip: 'Show limited list view',
  },
];

const EventTimelinePanel = memo((properties) => {
  const {
    site,
    views,
    limits: {
      time = 'past',
      range: { period = 'hours', numberPeriods = 1 } = {},
      limit: configuredLimitedListEventCount = 10,
    } = {},
    filters: panelFilters,
    isFullScreen,
    componentScope,
    format,
    panelId,
  } = properties;
  const { eventTypes } = panelFilters || {};
  const {
    types: panelEventTypes = [],
    machExcludeTypes = [],
    dtmpExcludeTypes = [],
    userExcludeTypes = [],
    machColumns: additionalMachColumns = [],
    userColumns: additionalUserColumns = [],
    dtmpColumns: additionalDtmpColumns = [],
    hideInactiveSystemEvents = true,
  } = eventTypes || {};

  const enabledEventTypes = useMemo(
    () => normalizeEventTypeSelection(panelEventTypes),
    [panelEventTypes]
  );
  // const hideInactiveSystemEvents = configuration.hideInactiveSystemEvents ?? true;
  const clampedLimitedListEventCount = clampLimitedListEventCount(configuredLimitedListEventCount);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(requestUserEventTypes());
    dispatch(requestTags(site.id));
    dispatch(requestSeriesEvents({ site: site.id, org: site.org, seriesOnly: true, order: 'asc' }));
    if (!config.onPremise) {
      dispatch(requestLimitedUsers(site.id));
    }
  }, [dispatch, site.id, site.org]);

  const importErrors = useSelector(getImportErrors);
  const siteComponents = useSelector(getStaticComponents);
  const isTenantAdmin = useSelector((state) => getIsTenantOrSuperAdmin(state, site.org));
  const isAdmin = useSelector((state) => getIsAdmin(state, site));
  const [importing, setImporting] = useState(false);
  const [maintenanceSeriesToCreate, setMaintenanceSeriesToCreate] = useState([]);
  const [maintenanceComponentsToCreate, setMaintenanceComponentsToCreate] = useState([]);
  const [missingComponents, setMissingComponents] = useState([]);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (importErrors.length > 0) {
      setImporting(false);
      setShowErrors(true);
    } else {
      setShowErrors(false);
    }
  }, [importErrors]);

  const allUserEventTypes = useSelector(getUserEventTypes);

  const allMachSeverities = useMemo(
    () =>
      Object.entries(SEVERITIES).map(([k, v]) => ({
        name: v,
        id: k,
      })),
    []
  );

  const allDtmpSeverities = useMemo(
    () =>
      Object.entries(DTMP_SEVERITIES).map(([k, v]) => ({
        name: v,
        id: k,
      })),
    []
  );

  const filteredMachSeverities = useMemo(
    () =>
      includesEventType(enabledEventTypes, EVENT_TYPE_MACH)
        ? allMachSeverities.filter((type) => !machExcludeTypes.includes(type.id))
        : [],
    [enabledEventTypes, allMachSeverities, machExcludeTypes]
  );

  const filteredDtmpSeverities = useMemo(
    () =>
      includesEventType(enabledEventTypes, EVENT_TYPE_DTMP)
        ? allDtmpSeverities.filter((type) => !dtmpExcludeTypes.includes(type.id))
        : [],
    [enabledEventTypes, allDtmpSeverities, dtmpExcludeTypes]
  );

  const availableIotEventTypes = useMemo(
    () => ({
      mach: filteredMachSeverities,
      dtmp: { eventTypes: filteredDtmpSeverities },
    }),
    [filteredMachSeverities, filteredDtmpSeverities]
  );

  const availableUserEventTypes = useMemo(
    () =>
      includesEventType(enabledEventTypes, EVENT_TYPE_USER)
        ? allUserEventTypes.filter((type) => !userExcludeTypes.includes(type.id))
        : [],
    [enabledEventTypes, allUserEventTypes, userExcludeTypes]
  );

  const additionalColumns = useMemo(() => {
    return [
      ...(includesEventType(enabledEventTypes, EVENT_TYPE_MACH) ? additionalMachColumns : []),
      ...(includesEventType(enabledEventTypes, EVENT_TYPE_DTMP) ? additionalDtmpColumns : []),
      ...(includesEventType(enabledEventTypes, EVENT_TYPE_USER) ? additionalUserColumns : []),
    ];
  }, [enabledEventTypes, additionalMachColumns, additionalDtmpColumns, additionalUserColumns]);

  const eventListColumns = useMemo(() => {
    const optionalEventColumns = [
      ...OPTIONAL_MACH_EVENT_COLUMNS,
      ...OPTIONAL_USER_EVENT_COLUMNS,
      ...OPTIONAL_DTMP_EVENT_COLUMNS,
    ];
    return optionalEventColumns.filter((c) => additionalColumns.includes(c.id));
  }, [additionalColumns]);

  const layouts = useMemo(
    () => panelViews.filter((view) => !views || views.includes(view.type)),
    [views]
  );
  const defaultLayout = useMemo(() => {
    if (format === 'narrow' && views?.includes(LIMITED_LIST_VIEW)) return LIMITED_LIST_VIEW;
    return layouts[0]?.type ?? panelViews[0].type;
  }, [format, views, layouts]);

  const [activeLayout, setActiveLayout] = useState(defaultLayout);

  useEffect(() => {
    if (!layouts.some((layout) => layout.type === activeLayout)) {
      setActiveLayout(defaultLayout);
    }
  }, [defaultLayout]);

  const onSetLayout = useCallback((layoutType) => {
    setActiveLayout(layoutType);
  }, []);

  const [selectedEventType, setSelectedEventType] = useState(() =>
    getDefaultEventType(enabledEventTypes)
  );
  const onEventTypeChange = useCallback((eventType) => setSelectedEventType(eventType), []);

  useEffect(() => {
    if (!enabledEventTypes.includes(selectedEventType)) {
      setSelectedEventType(getDefaultEventType(enabledEventTypes));
    }
  }, [enabledEventTypes, selectedEventType]);

  const [{ width, height }, clientReference] = useClientSize();
  const [dateDomain, refresh] = useDateDomainSource(panelId);
  const userEventTypes = useUserEventTypes();

  const sortedUserEventTypes = useMemo(
    () =>
      (userEventTypes || []).toSorted(({ name: nameA }, { name: nameB }) =>
        nameA.localeCompare(nameB)
      ),
    [userEventTypes]
  );

  const [eventDetail, onEventDetail] = useEventModal();
  const [fileUpload, onFileUpload] = useEventModal();

  const handleClose = useCallback(
    (options = {}) => {
      // Check if options is actually an event object (from modal close button)
      const isEvent = options?.nativeEvent || options?.type === 'click';
      const { getSeries = false, refresh: shouldRefresh = false } = isEvent ? {} : options;
      if (getSeries) {
        dispatch(
          requestSeriesEvents(
            { site: site.id, org: site.org, seriesOnly: true, order: 'asc' },
            refresh
          )
        );
      } else if (shouldRefresh) {
        refresh();
      }
      setImporting(false);
    },
    [dispatch, site.id, site.org, refresh]
  );

  const onOpenNewEvent = useCallback(() => {
    onEventDetail({
      modalType: EVENT_TYPE_USER,
      dateDomain,
      userEventTypes: sortedUserEventTypes,
      site: { id: site.id, org: site.org },
      onClose: handleClose,
      componentScope,
    });
  }, [
    onEventDetail,
    dateDomain,
    sortedUserEventTypes,
    site.id,
    site.org,
    handleClose,
    componentScope,
  ]);

  const onOpenEvent = useCallback(
    (item) => {
      const { event, type, id, componentName } = item;
      onEventDetail({
        modalType: type,
        event,
        eventId: id,
        dateDomain,
        userEventTypes: sortedUserEventTypes,
        site: { id: site.id, org: site.org },
        onDelete: refresh,
        onClose: handleClose,
        hideInactiveSystemEvents,
        eventListColumns,
        componentName,
      });
    },
    [
      onEventDetail,
      dateDomain,
      sortedUserEventTypes,
      site.id,
      site.org,
      refresh,
      handleClose,
      hideInactiveSystemEvents,
      eventListColumns,
    ]
  );

  async function importMaintenanecePlan(file) {
    const [maintenanceSeries, maintenanceComponents, missingComps, maintenanceErrors] =
      await parseMaintenanceExcel({
        file,
        components: siteComponents,
        site: site.id,
        org: site.org,
      });

    if (maintenanceErrors.length > 0) {
      dispatch(setImportErrors(maintenanceErrors));
    } else {
      setMaintenanceSeriesToCreate(maintenanceSeries);
      setMaintenanceComponentsToCreate(maintenanceComponents);
      setMissingComponents(missingComps);
      setImporting(true);
    }
  }

  const onDrop = (accepted) => {
    if (accepted.length > 0) {
      if (
        accepted[0].type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) {
        importMaintenanecePlan(accepted[0]);
      } else {
        onFileUpload({ modalType: 'file', file: accepted[0], isTenantAdmin });
      }
    } else {
      dispatch(
        setImportErrors([
          'Imports should be from a single Excel file for maintenance plans or a single XML file for import of IoT Events',
        ])
      );
    }
  };

  const xmlMimes = {
    'application/xml': [],
    'text/xml': [],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled: !isAdmin,
    noClick: true,
    accept: xmlMimes,
    multiple: false,
    noKeyboard: true,
    onDrop,
  });

  const getPeriod = useCallback(() => {
    const periodToMillis = {
      minutes: 1000 * 60,
      hours: 1000 * 60 * 60,
      days: 1000 * 60 * 60 * 24,
    };
    const now = Date.now();
    if (time === 'past') {
      return [now - periodToMillis[period] * numberPeriods, now];
    }
    return [now, now + periodToMillis[period] * numberPeriods];
  }, [time, period, numberPeriods]);

  const isNarrowUnsupported = format === 'narrow' && activeLayout !== LIMITED_LIST_VIEW;

  const isLimitedListView = useMemo(() => activeLayout === LIMITED_LIST_VIEW, [activeLayout]);

  const [limitedListDateDomain, setLimitedListDateDomain] = useState(getPeriod());
  const pollingInterval = useSelector(getPollingInterval);
  const userRefresh = useSelector(getLastRefresh);

  useEffect(() => {
    let interval;
    if (pollingInterval || userRefresh) {
      let intervalCallback = () => refresh();
      if (isLimitedListView) {
        setLimitedListDateDomain(() => getPeriod());
        intervalCallback = () => setLimitedListDateDomain(() => getPeriod());
      } else {
        refresh();
      }
      if (pollingInterval !== 0) {
        interval = setInterval(intervalCallback, pollingInterval);
      }
    }
    return () => clearInterval(interval);
  }, [pollingInterval, userRefresh, getPeriod, isLimitedListView, refresh]);

  const timelineBucketCount = width > 200 ? Math.floor((width - 100) / 10) : 10;
  const brushBucketCount = width > 200 ? Math.floor((width - 100) / 10) : 10;

  // 377 width of narrow panel @ screen 1330 (event list item breakpoint)
  // 330 width of narrow panel @ screen 1200 (font-size breakpoint)
  let itemHeight = 67.5;
  if (isLimitedListView && width < 377) {
    itemHeight = width < 330 ? 85.5 : 89;
  }

  const getListHeight = () => {
    if (isLimitedListView) {
      return isFullScreen ? height : clampedLimitedListEventCount * itemHeight;
    }
    if (!isFullScreen) {
      if (activeLayout === SPLIT_VIEW) return 300;
      if (activeLayout === LIST_VIEW) return NON_FULLSCREEN_LIST_HEIGHT;
    }
    // Full-screen responsive LIST height
    return height;
  };

  if (isNarrowUnsupported) {
    return (
      <div className="event-timeline-outer-container event-timeline-narrow-unsupported">
        <p>
          Narrow panels only support the limited list event type. <br />
          Change the view type for events in the panel settings
        </p>
      </div>
    );
  }

  if (isLimitedListView) {
    return (
      <EventTimelineProvider
        site={site}
        dateDomain={limitedListDateDomain}
        timelineBucketCount={timelineBucketCount}
        brushBucketCount={brushBucketCount}
        componentScope={componentScope}
        eventListOrdering={time === 'past' ? 'desc' : 'asc'}
        availableIotEventTypes={availableIotEventTypes}
        availableUserEventTypes={availableUserEventTypes}
        hideInactiveSystemEvents={hideInactiveSystemEvents}
        selectedEventType={selectedEventType}
        enabledEventTypes={enabledEventTypes}
      >
        <div className="event-timeline-panel">
          <div
            ref={clientReference}
            className="event-timeline-outer-container"
          >
            <LoadingLayer>
              <div className="event-timeline-list-container limited custom-scrollbar">
                <EventList
                  width={width}
                  height={getListHeight()}
                  itemSize={itemHeight}
                  onOpenEvent={onOpenEvent}
                  limit={clampedLimitedListEventCount}
                  time={time}
                  format={format}
                  hideInactiveSystemEvents={hideInactiveSystemEvents}
                  allUserEventTypes={allUserEventTypes}
                />
              </div>
            </LoadingLayer>
          </div>
        </div>
        {eventDetail}
      </EventTimelineProvider>
    );
  }

  const buildEventTimelines = (timelineType) => {
    switch (timelineType) {
      case EVENT_TYPE_USER: {
        return (
          <UserTimeline
            fullWidth={activeLayout === LIST_VIEW}
            onOpenEvent={onOpenEvent}
          />
        );
      }
      case EVENT_TYPE_DTMP:
      case EVENT_TYPE_MACH: {
        return (
          <IotTimeline
            fullWidth={activeLayout === LIST_VIEW}
            onOpenEvent={onOpenEvent}
          />
        );
      }
    }
  };
  return (
    <EventTimelineProvider
      site={site}
      dateDomain={dateDomain}
      timelineBucketCount={timelineBucketCount}
      brushBucketCount={brushBucketCount}
      componentScope={componentScope}
      availableIotEventTypes={availableIotEventTypes}
      availableUserEventTypes={availableUserEventTypes}
      hideInactiveSystemEvents={hideInactiveSystemEvents}
      selectedEventType={selectedEventType}
      enabledEventTypes={enabledEventTypes}
    >
      <div className="event-timeline-panel">
        <EventTimelineHeader
          format={format}
          layouts={layouts}
          activeLayout={activeLayout}
          onSetLayout={onSetLayout}
          onCreateEvent={onOpenNewEvent}
          width={width}
          isFullScreen={isFullScreen}
          panelId={panelId}
          availableIotEventTypes={availableIotEventTypes}
          availableUserEventTypes={availableUserEventTypes}
          allUserEventTypes={allUserEventTypes}
          onEventTypeChange={onEventTypeChange}
          selectedEventType={selectedEventType}
          enabledEventTypes={enabledEventTypes}
        />
        <div
          ref={clientReference}
          className="event-timeline-outer-container"
        >
          <LoadingLayer>
            <div
              {...getRootProps()}
              className="drop-wrapper"
            >
              {isDragActive && (isTenantAdmin || isAdmin) && (
                <>
                  <input {...getInputProps()} />
                  <div className="drop-zone">
                    <p>Drop an event XML file here...</p>
                  </div>
                </>
              )}
              {activeLayout !== LIST_VIEW && (
                <div className="event-timeline-view">{buildEventTimelines(selectedEventType)}</div>
              )}
              {activeLayout !== LIST_VIEW && (
                <div className="event-timeline-brush">
                  <EventBrush type={selectedEventType} />
                </div>
              )}
              {activeLayout !== TIMELINE_VIEW && (
                <>
                  <EventLegends
                    fullWidth
                    eventType={selectedEventType}
                    showLegend={false}
                  />
                  <div
                    className="event-timeline-list-container custom-scrollbar"
                    style={{
                      display: activeLayout === TIMELINE_VIEW ? 'none' : 'block',
                    }}
                  >
                    <EventList
                      width={width}
                      height={getListHeight()}
                      onOpenEvent={onOpenEvent}
                      listColumns={eventListColumns}
                      layoutName={activeLayout}
                      hideInactiveSystemEvents={hideInactiveSystemEvents}
                      allUserEventTypes={allUserEventTypes}
                    />
                  </div>
                </>
              )}
            </div>
            {importing && (
              <ImportMaintenancePlanModal
                onClose={handleClose}
                site={site}
                maintenanceSeriesToCreate={maintenanceSeriesToCreate}
                maintenanceComponentsToCreate={maintenanceComponentsToCreate}
                missingComponents={missingComponents}
              />
            )}
            {showErrors ? (
              <ConfirmationDialog
                onConfirm={() => {
                  dispatch(setImportErrors([]));
                }}
                onCancel={() => {
                  dispatch(setImportErrors([]));
                }}
                showCancel={false}
                title="Import errors"
                subtitle="The following errors occurred while attempting import:"
                body={importErrors.map((error, index) => (
                  <p key={`${error}-${index}`}>{error}</p>
                ))}
                className="setup-wizard-errors"
              />
            ) : null}
          </LoadingLayer>
        </div>
      </div>
      {eventDetail}
      {fileUpload}
    </EventTimelineProvider>
  );
});
export default EventTimelinePanel;
